// AppSidebar.jsx
import { useState, useRef } from "react";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme, Tooltip } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { ReviewsOutlined } from "@mui/icons-material";
import PeopleOutinedIcon from "@mui/icons-material/PeopleOutlined";

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <MenuItem
      active={selected === title}
      icon={icon}
      component={<Link to={to} />}
      onClick={() => setSelected(title)}
      style={{ color: colors.grey[100] }}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const AppSidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [collapsed, setCollapsed] = useState(false);
  const [selected, setSelected] = useState(location.pathname);
  const [profilePic, setProfilePic] = useState(
    localStorage.getItem("adminProfilePic") || null,
  );
  const [hovering, setHovering] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      localStorage.setItem("adminProfilePic", base64);
      setProfilePic(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePic = (e) => {
    e.stopPropagation();
    localStorage.removeItem("adminProfilePic");
    setProfilePic(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box
      sx={{
        height: "100vh",
        "& .ps-menu-button:hover": {
          backgroundColor: "transparent !important",
          color: "#868dfb !important",
        },
        "& .ps-menuitem-root.active": {
          color: "#6870fa !important",
        },
      }}
    >
      {/* backgroundColor prop is reactive to theme changes — no !important override needed */}
      <Sidebar
        collapsed={collapsed}
        backgroundColor={colors.primary[400]}
        rootStyles={{ border: "none" }}
      >
        <Menu>
          {/* HEADER */}
          <MenuItem
            onClick={() => setCollapsed(!collapsed)}
            style={{ margin: "10px 0 20px 0", color: colors.grey[100] }}
          >
            {!collapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="10px"
              >
                <Typography variant="h3">ADMIN</Typography>
                <IconButton onClick={() => setCollapsed(!collapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {/* PROFILE SECTION */}
          {!collapsed && (
            <Box mb="25px" textAlign="center">
              {/* Avatar with hover overlay */}
              <Box
                position="relative"
                display="inline-block"
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                sx={{ cursor: "pointer" }}
                onClick={() => fileInputRef.current?.click()}
              >
                {/* Avatar */}
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: `3px solid ${colors.greenAccent[500]}`,
                    mx: "auto",
                    backgroundColor: colors.primary[300],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                    boxShadow: hovering
                      ? `0 0 0 4px ${colors.greenAccent[700]}`
                      : "none",
                  }}
                >
                  {profilePic ? (
                    <img
                      src={profilePic}
                      alt="profile"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 700,
                        color: colors.greenAccent[400],
                        userSelect: "none",
                      }}
                    >
                      A
                    </Typography>
                  )}
                </Box>

                {/* Hover overlay */}
                {hovering && (
                  <Box
                    position="absolute"
                    top={0}
                    left="50%"
                    sx={{
                      transform: "translateX(-50%)",
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      backgroundColor: "rgba(0,0,0,0.55)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <AddAPhotoOutlinedIcon
                      sx={{ color: "#fff", fontSize: 22 }}
                    />
                    <Typography
                      sx={{ color: "#fff", fontSize: "9px", fontWeight: 600 }}
                    >
                      {profilePic ? "Change" : "Upload"}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                hidden
              />

              {/* Remove button — only shown if pic exists */}
              {profilePic && (
                <Tooltip title="Remove photo">
                  <IconButton
                    size="small"
                    onClick={handleRemovePic}
                    sx={{
                      mt: 0.5,
                      color: colors.grey[400],
                      "&:hover": { color: "#ef4444" },
                      transition: "color 0.2s",
                    }}
                  >
                    <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}

              <Typography
                variant="h2"
                fontWeight="bold"
                sx={{ mt: profilePic ? 0 : 1 }}
                color={colors.grey[100]}
              >
                Akoth Adero
              </Typography>
              <Typography variant="h5" color={colors.greenAccent[500]}>
                AkothAdero Admin
              </Typography>
            </Box>
          )}

          {/* Collapsed avatar */}
          {collapsed && (
            <Box display="flex" justifyContent="center" mb={2} mt={1}>
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `2px solid ${colors.greenAccent[500]}`,
                  cursor: "pointer",
                  backgroundColor: colors.primary[300],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {profilePic ? (
                  <img
                    src={profilePic}
                    alt="profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: colors.greenAccent[400],
                    }}
                  >
                    A
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* MENU ITEMS */}
          <Item
            title="Dashboard"
            to="/"
            icon={<HomeOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />

          <Typography sx={{ m: "15px 0 5px 20px" }} color={colors.grey[300]}>
            Data
          </Typography>

          <Item
            title="Books"
            to="/books"
            icon={<MenuBookOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="Invoices"
            to="/invoices"
            icon={<ReceiptOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />

          <Typography sx={{ m: "15px 0 5px 20px" }} color={colors.grey[300]}>
            Pages
          </Typography>

          <Item
            title="AddBook"
            to="/add-book"
            icon={<MenuBookOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="AddEvent"
            to="/add-event"
            icon={<EventOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="AddGalleryItem"
            to="/add-gallery-item"
            icon={<CollectionsOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="Calendar"
            to="/calendar"
            icon={<CalendarTodayOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="Reviews"
            to="/reviews"
            icon={<ReviewsOutlined />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="Leads"
            to="/leads"
            icon={<PeopleOutinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />

          <Typography sx={{ m: "15px 0 5px 20px" }} color={colors.grey[300]}>
            Charts
          </Typography>

          <Item
            title="Bar"
            to="/bar"
            icon={<BarChartOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="Pie"
            to="/pie"
            icon={<PieChartOutlineOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="Line"
            to="/line"
            icon={<TimelineOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
          <Item
            title="Geography"
            to="/geography"
            icon={<MapOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
          />
        </Menu>
      </Sidebar>
    </Box>
  );
};

export default AppSidebar;
